import type { OrderWithTimeline } from "../page";

interface Props {
  order: OrderWithTimeline;
}

export default function OrderSummaryCard({ order }: Props) {
  const totalSavings = order.discountAmount + (order.redeemCoupon?.discountAmount ?? 0);

  return (
    <div className="bg-[#141414] border border-white/5 rounded-2xl p-6 flex flex-col">
      <h2 className="text-white font-bold text-sm mb-5 flex items-center gap-2" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
          <i className="ri-receipt-line text-amber-400 text-sm"></i>
        </div>
        ملخص الطلب
      </h2>

      <div className="flex flex-col gap-3 flex-1">
        {/* Subtotal */}
        <div className="flex justify-between items-center">
          <span className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>المجموع الفرعي</span>
          <span className="text-gray-300 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
            {order.totalPrice.toLocaleString()} ر.س
          </span>
        </div>

        {/* Discount code */}
        {order.discountAmount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-gray-500 text-sm flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-coupon-3-line text-green-400/70"></i>
              خصم
              {order.discountCode && (
                <span className="bg-green-400/10 text-green-400 text-[11px] font-bold px-2 py-0.5 rounded-full border border-green-400/20">
                  {order.discountCode}
                </span>
              )}
            </span>
            <span className="text-green-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {order.discountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        {/* Redeem coupon */}
        {order.redeemCoupon && (
          <div className="flex justify-between items-center">
            <span className="text-gray-500 text-sm flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-exchange-dollar-line text-amber-400/70"></i>
              كوبون نقاط
              <span className="bg-amber-400/10 text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-400/20 font-mono">
                {order.redeemCoupon.couponCode}
              </span>
            </span>
            <span className="text-amber-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              — {order.redeemCoupon.discountAmount.toLocaleString()} ر.س
            </span>
          </div>
        )}

        {/* Shipping */}
        <div className="flex justify-between items-center">
          <span className="text-gray-500 text-sm flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-truck-line text-gray-600"></i>
            الشحن
          </span>
          <span className="text-green-400 text-sm font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>مجاني</span>
        </div>

        {/* Payment method */}
        <div className="flex justify-between items-center">
          <span className="text-gray-500 text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>طريقة الدفع</span>
          <span className="text-gray-300 text-sm font-bold flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
            <i className="ri-hand-coin-line text-amber-400/60 text-base"></i>
            {order.paymentLabel}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 my-1"></div>

        {/* Total */}
        <div className="flex justify-between items-center">
          <span className="text-white font-bold text-base" style={{ fontFamily: "'Cairo', sans-serif" }}>الإجمالي</span>
          <div className="text-left">
            <span className="text-amber-400 font-black text-xl" style={{ fontFamily: "'Cairo', sans-serif" }}>
              {order.finalPrice.toLocaleString()}
            </span>
            <span className="text-amber-400/70 text-sm mr-1" style={{ fontFamily: "'Cairo', sans-serif" }}>ر.س</span>
          </div>
        </div>

        {/* Savings badge */}
        {totalSavings > 0 && (
          <div className="mt-1 flex items-center gap-2 bg-green-400/5 border border-green-400/15 rounded-xl px-3 py-2.5">
            <i className="ri-price-tag-3-line text-green-400 text-sm"></i>
            <p className="text-green-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              وفّرت {totalSavings.toLocaleString()} ر.س في هذا الطلب!
            </p>
          </div>
        )}

        {/* Points earned badge */}
        {order.pointsEarned && order.pointsEarned > 0 && (
          <div className="flex items-center gap-2 bg-amber-400/5 border border-amber-400/15 rounded-xl px-3 py-2.5">
            <i className="ri-vip-crown-line text-amber-400 text-sm"></i>
            <p className="text-amber-400 text-xs font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>
              كسبت <span className="text-base">+{order.pointsEarned}</span> نقطة مكافأة من هذا الطلب
            </p>
          </div>
        )}

        {/* Redeem coupon detail */}
        {order.redeemCoupon && (
          <div className="bg-[#1a1a1a] border border-amber-400/15 rounded-xl px-3 py-3 flex flex-col gap-1.5">
            <p className="text-amber-400/80 text-[11px] font-bold flex items-center gap-1.5" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <i className="ri-exchange-dollar-line text-xs"></i>
              تفاصيل كوبون الاستبدال
            </p>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>الكود</span>
              <span className="text-gray-300 text-[11px] font-mono font-bold">{order.redeemCoupon.couponCode}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>نسبة الخصم</span>
              <span className="text-amber-400 text-[11px] font-bold">{order.redeemCoupon.discountPercent}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600 text-[11px]" style={{ fontFamily: "'Cairo', sans-serif" }}>الحالة</span>
              <span className="flex items-center gap-1 text-[11px] font-bold text-gray-400" style={{ fontFamily: "'Cairo', sans-serif" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-500 inline-block"></span>
                مستخدم
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
